import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import Video from "../components/Video";
import Magnetic from "../components/Magnetic";
import { V, CALENDLY } from "../data/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yHead = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const scaleHead = useTransform(scrollYProgress, [0, 1], [1, 0.86]);
  const opHead = useTransform(scrollYProgress, [0, 0.7, 1], [1, 1, 0]);
  const vidOp = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.28, 0.55]);
  const vidScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const gap = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const cueOp = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  // Mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sxSlow = useSpring(mx, { stiffness: 60, damping: 20 });
  const sySlow = useSpring(my, { stiffness: 60, damping: 20 });
  const whiteX = useTransform(sxSlow, [-1, 1], [-10, 10]);
  const whiteY = useTransform(sySlow, [-1, 1], [-6, 6]);
  const lavX = useTransform(sxSlow, [-1, 1], [22, -22]);
  const lavY = useTransform(sySlow, [-1, 1], [14, -14]);

  const onMove = (e) => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    mx.set((e.clientX / w) * 2 - 1);
    my.set((e.clientY / h) * 2 - 1);
  };

  return (
    <section
      ref={ref}
      id="top"
      onMouseMove={onMove}
      className="relative h-[100svh] w-full overflow-hidden"
      data-testid="hero"
    >
      {/* Background video fragments (fade in on scroll) */}
      <motion.div
        style={{ opacity: vidOp, scale: vidScale }}
        className="pointer-events-none absolute inset-0 z-0"
      >
        <div className="absolute left-[6%] top-[16%] h-[42vh] w-[13vw] min-w-[140px]">
          <Video src={V.motion1} priority className="h-full w-full" />
        </div>
        <div className="absolute right-[7%] top-[20%] h-[44vh] w-[13vw] min-w-[140px]">
          <Video src={V.talking} priority className="h-full w-full" />
        </div>
        <div className="absolute bottom-[8%] left-[24%] h-[30vh] w-[10vw] min-w-[120px]">
          <Video src={V.faceless} className="h-full w-full" />
        </div>
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      {/* Headline */}
      <motion.div
        style={{ y: yHead, scale: scaleHead, opacity: opHead }}
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
      >
        <motion.h1
          className="text-display uppercase"
          style={{ x: whiteX, y: whiteY }}
        >
          <span className="block">VIEWS DON'T</span>
          <span className="block">PAY YOU.</span>
        </motion.h1>

        <motion.div style={{ marginTop: gap }} className="mt-2">
          <motion.h1
            className="text-display uppercase text-lav-400"
            style={{ x: lavX, y: lavY }}
          >
            REVENUE DOES.
          </motion.h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl text-sm md:text-base font-light leading-relaxed text-white/55"
        >
          We build content systems engineered for attention, retention and
          conversion.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-6"
        >
          <a
            href="#work"
            data-cursor="OPEN"
            data-testid="hero-cta-work"
            className="label group flex items-center gap-2 text-white"
          >
            <span className="border-b border-transparent pb-1 transition-colors group-hover:border-lav-400 group-hover:text-lav-400">
              VIEW OUR WORK ↗
            </span>
          </a>
          <Magnetic strength={0.35}>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noreferrer"
              data-cursor="OPEN"
              data-testid="hero-cta-talk"
              className="label group relative inline-flex items-center overflow-hidden border border-white/25 px-6 py-3 text-white"
            >
              <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                LET'S TALK ↗
              </span>
              <span className="absolute inset-0 z-0 -translate-y-full bg-lav-400 transition-transform duration-500 ease-lux group-hover:translate-y-0" />
            </a>
          </Magnetic>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        style={{ opacity: cueOp }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 label text-white/40"
      >
        SCROLL
      </motion.div>
    </section>
  );
}
